import { Training } from "@/components/Trains";
import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/shared/utils/baseQuery";

type EditTrainingDateRequest = {
  trainig_id: number;
  old_date: string;
  new_date: string;
};

type GetAlternativesExercizesRequest = {
  exercize_id: number;
};

type ReplaceExercizeOnTrainingRequest = {
  training_id: number;
  old_exercize_id: number;
  new_exercize_id: number;
};

type GetExercizesRequest = {
  training_id: number;
  training_type: 'free' | 'schedule';
  fromUser?: number;
};

export type ExercizeObj = {
  id: number;
  title: string;
  description?: string;
  title_photo?: string;
  video_file?: string;
  of_date?: string;
  muscle_group?: string;
  documentation?: string;
  results?: any[];
  config?: any[];
};

type GetExercizesResponse = {
  user_id: number;
  exercizes: ExercizeObj[];
  environment?: string;
  training_title?: string;
};

type GetExercizeByTrainingRequest = {
  training_id: number;
  exercize_id: number;
  training_type: 'free' | 'schedule';
  tse: number;
};

type ApproachObj = {
  approach_number: number;
  repetitions: number;
  repetitions_margin: number;
  procent_of_maximum_weight: number;
  duration: number;
};

type ExercizeInfo = {
  title: string;
  title_photo: string;
  description: string;
  video_url: string;
};

type LastResultExercize = {
  approach_number: number;
  schedule_id: number;
  repeats: number;
  weight: number | null;
  of_date: string;
};

type GetExercizeByTrainingResponse = {
  exercize: ExercizeInfo;
  approaches: ApproachObj[];
  break_duration: number;
  next_exercize_id?: number | null;
  prev_exercize_id?: number | null;
  lastResult?: LastResultExercize[] | null;
  user_max_weight?: number | null;
};

type ApproachResultObj = {
  repetitions: number;
  weight: number;
  approach_number: number;
};

type AddExercizeResultRequest = {
  training_id: number;
  exercize_id: number;
  approachs: ApproachResultObj[];
  training_complete?: boolean;
};

type SetMaxWeightRequest = {
  exercize_id: number;
  weight: number;
};

type AddExercizeResultResponse = {
  status: string;
};

type CreateTrainingRequest = {
  title: string;
  training_date: string;
  muscle_group?: string;
  fromUser?: number;
};

type CreateTrainingResponse = {
  training_id: number;
};

type CreateApproachObj = {
  approach_number: number;
  repetitions: number;
  repetitions_margin?: number;
  weight?: string;
};

type AddExercizeToTrainingRequest = {
  approaches: CreateApproachObj[];
  fromUser?: number;
};

type AddExercizeToTrainingResponse = {
  status: string;
};

type SearchExercizesResponse = ExercizeObj[];

type GetTrainingsRequest = {
  fromUser?: number;
};

export const trainingsApi = createApi({
  reducerPath: "trainingsApi",
  baseQuery: createBaseQuery("https://api.fitkeep.ru/"),
  tagTypes: ["Trainings", "Exercizes"],
  endpoints: (builder) => ({
    createTraining: builder.mutation<any, GetTrainingsRequest | void>({
      query: (body) => ({
        url: "/trainings",
        method: "POST",
        body: {},
        params: body?.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings"],
    }),
    createExercize: builder.mutation({
      query: (body) => ({
        url: "/trainings/exercizes",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exercizes"],
    }),
    editTrainingDate: builder.mutation<void, EditTrainingDateRequest>({
      query: (body) => ({
        url: "/trainings/edit/date",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Trainings"],
    }),
    getAlternativesExercizes: builder.mutation({
      query: (body: GetAlternativesExercizesRequest) => ({
        url: "/trainings/exercizes/alternatives",
        method: "POST",
        body,
      }),
    }),
    replaceExercizeOnTraining: builder.mutation({
      query: (body: ReplaceExercizeOnTrainingRequest) => ({
        url: "/trainings/exercizes/replace",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exercizes"],
    }),
    getExercizes: builder.mutation<GetExercizesResponse, GetExercizesRequest>({
      query: (body) => ({
        url: "/trainings/exercizes",
        method: "POST",
        body: {
          training_id: body.training_id,
          training_type: body.training_type,
        },
        params: body.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Exercizes"],
    }),
    getExercizeByTraining: builder.mutation<GetExercizeByTrainingResponse, GetExercizeByTrainingRequest>({
      query: (body) => ({
        url: "/trainings/exercizes/start",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exercizes"],
    }),
    addExercizeResult: builder.mutation<AddExercizeResultResponse, AddExercizeResultRequest>({
      query: (body) => ({
        url: "/trainings/exercizes/result",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exercizes"],
    }),
    createUserTraining: builder.mutation<CreateTrainingResponse, CreateTrainingRequest>({
      query: (body) => ({
        url: "/trainings/create",
        method: "POST",
        body: {
          title: body.title,
          training_date: body.training_date,
          muscle_group: body.muscle_group,
        },
        params: body.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings"],
    }),
    searchExercizes: builder.query<SearchExercizesResponse, { q?: string }>({
      query: (params) => ({
        url: "/exercizes/search",
        method: "GET",
        params,
      }),
    }),
    addExercizeToTraining: builder.mutation<AddExercizeToTrainingResponse, { training_id: number; exercize_id: number; body: AddExercizeToTrainingRequest }>({
      query: ({ training_id, exercize_id, body }) => ({
        url: `/trainings/${training_id}/exercizes/${exercize_id}/add`,
        method: "POST",
        body: {
          approaches: body.approaches,
        },
        params: body.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings", "Exercizes"],
    }),
    removeExercizeFromTraining: builder.mutation<{ status: string }, { training_id: number; exercize_id: number; fromUser?: number }>({
      query: (body) => ({
        url: `/trainings/${body.training_id}/exercizes/${body.exercize_id}/remove`,
        method: "POST",
        body: {},
        params: body.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings", "Exercizes"],
    }),
    deleteTraining: builder.mutation<{ status: string }, { training_id: number; fromUser?: number }>({
      query: ({ training_id, fromUser }) => ({
        url: `/trainings/${training_id}/remove`,
        method: "POST",
        body: {},
        params: fromUser ? { fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings"],
    }),
    editExercizeInTraining: builder.mutation<{ status: string }, { training_id: number; exercize_id: number; body: AddExercizeToTrainingRequest }>({
      query: ({ training_id, exercize_id, body }) => ({
        url: `/trainings/${training_id}/exercizes/${exercize_id}/edit`,
        method: "POST",
        body: {
          approaches: body.approaches,
        },
        params: body.fromUser ? { fromUser: body.fromUser } : undefined,
      }),
      invalidatesTags: ["Trainings", "Exercizes"],
    }),
    setExercizeMaxWeight: builder.mutation<{ status: string }, SetMaxWeightRequest>({
      query: (body) => ({
        url: '/trainings/exercizes/max-weight',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useCreateTrainingMutation,
  useCreateExercizeMutation,
  useEditTrainingDateMutation,
  useGetAlternativesExercizesMutation,
  useReplaceExercizeOnTrainingMutation,
  useGetExercizesMutation,
  useGetExercizeByTrainingMutation,
  useAddExercizeResultMutation,
  useCreateUserTrainingMutation,
  useSearchExercizesQuery,
  useLazySearchExercizesQuery,
  useAddExercizeToTrainingMutation,
  useRemoveExercizeFromTrainingMutation,
  useDeleteTrainingMutation,
  useEditExercizeInTrainingMutation,
  useSetExercizeMaxWeightMutation,
} = trainingsApi;
